import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { scenarioPacks as api } from '../lib/api.js'

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.lg};
`

const titleRow = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${space.lg};
`

const packGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${space.md};
`

const packCard = css`
  ${card};
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: ${colors.brand}; }
`

const packLabel = css`
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.xs};
`

const packDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.sm};
`

const packMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  display: flex;
  gap: ${space.md};
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

const inlineForm = css`
  ${card};
  margin-bottom: ${space.lg};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

export function ScenarioPacksView() {
  let projectId
  let packs = []
  let loading = true
  let error = null
  let showCreate = false
  let form = { label: '', description: '' }
  let saving = false

  async function load() {
    loading = true
    error = null
    try {
      packs = await api.list(projectId)
    } catch (e) {
      error = e.message
    }
    loading = false
    m.redraw()
  }

  async function createPack(e) {
    e.preventDefault()
    if (!form.label.trim()) return
    saving = true
    try {
      await api.create(projectId, {
        label: form.label.trim(),
        description: form.description.trim() || undefined,
      })
      showCreate = false
      form = { label: '', description: '' }
      await load()
    } catch (e) {
      error = e.message
    }
    saving = false
    m.redraw()
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      load()
    },
    view() {
      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, 'Project'),
          ' / Scenarios',
        ]),

        m('div', { class: titleRow }, [
          m('h1', { class: pageTitle }, 'Scenario Packs'),
          m('button', {
            class: showCreate ? btnSecondary : btnPrimary,
            onclick: () => { showCreate = !showCreate },
          }, showCreate ? 'Cancel' : 'Create Pack'),
        ]),

        error ? m('div', {
          style: `color: ${colors.critical}; margin-bottom: ${space.md};`,
        }, error) : null,

        // Inline create form
        showCreate ? m('form', {
          class: inlineForm,
          onsubmit: createPack,
        }, [
          m('div', { class: formRow }, [
            m('label', { class: label }, 'Label'),
            m('input', {
              class: input,
              value: form.label,
              oninput: (e) => { form.label = e.target.value },
              placeholder: 'e.g., Tax Scenarios',
              autofocus: true,
            }),
          ]),
          m('div', { class: formRow }, [
            m('label', { class: label }, 'Description (optional)'),
            m('textarea', {
              class: input,
              rows: 3,
              value: form.description,
              oninput: (e) => { form.description = e.target.value },
              placeholder: 'What does this pack cover?',
            }),
          ]),
          m('div', { class: formActions }, [
            m('button', {
              class: btnPrimary,
              type: 'submit',
              disabled: saving || !form.label.trim(),
            }, saving ? 'Creating...' : 'Create'),
          ]),
        ]) : null,

        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : packs.length === 0
            ? m('div', { class: emptyState }, [
                m('p', 'No scenario packs yet.'),
                m('p', { style: `font-size: ${font.size.sm}; margin-top: ${space.sm};` },
                  'Create a pack to start building test scenarios.'),
              ])
            : m('div', { class: packGrid },
                packs.map(pack =>
                  m('div', {
                    class: packCard,
                    key: pack.id,
                    onclick: () => m.route.set(`/projects/${projectId}/scenarios/${pack.id}`),
                  }, [
                    m('div', { class: packLabel }, pack.label),
                    pack.description
                      ? m('div', { class: packDesc }, pack.description)
                      : null,
                    m('div', { class: packMeta }, [
                      m('span', `${pack.scenarioCount || 0} scenarios`),
                      m('span', `${pack.detailCount || 0} details`),
                      m('span', `${pack.generationCount || 0} generations`),
                    ]),
                  ])
                )
              ),
      ])
    },
  }
}
